import LocationPicker from '../../components/LocationPicker';
import BusinessHoursFields from '../../components/BusinessHoursFields';
import PrivateDocumentPhoto from '../../components/PrivateDocumentPhoto';
import useStoreCategories from '../../lib/useStoreCategories';
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { ImagePlus, Store, Upload, X, LoaderCircle } from 'lucide-react';
import styles from '../../styles/Onboarding.module.css';
import { post } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const Onboarding = ({ onComplete, initialData = {} }) => {
  const { categories: PRODUCT_CATEGORIES } = useStoreCategories();
  const { user } = useAuth();

  const [storeName, setStoreName] = useState(initialData.storeName || '');
  const [vendorCategory, setVendorCategory] = useState(initialData.vendorCategory || '');
  const [description, setDescription] = useState(initialData.description || '');
  const [businessTinNumber, setBusinessTinNumber] = useState(initialData.businessTinNumber || '');
  const [dtiRegistrationNumber, setDtiRegistrationNumber] = useState(initialData.dtiRegistrationNumber || '');
  const [permitNumber, setPermitNumber] = useState(initialData.permitNumber || '');
  const [businessHours, setBusinessHours] = useState(initialData.businessHours?.open ? initialData.businessHours : { open: '08:00', close: '22:00' });
  const [showRegistrationDetails] = useState(Boolean(initialData.showRegistrationDetails));
  const [documentLabels, setDocumentLabels] = useState([]);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [documentPreviews, setDocumentPreviews] = useState([]);
  useEffect(() => {
    const urls = documentFiles.map((file) => URL.createObjectURL(file));
    setDocumentPreviews(urls);
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, [documentFiles]);
  const [pickupLocation, setPickupLocation] = useState(initialData.pickupLocation);
  const [address, setAddress] = useState(initialData.completeAddress || '');

  const [logo, setLogo] = useState(null);
  const [banner, setBanner] = useState(null);

  const [logoPreview, setLogoPreview] = useState(initialData.logo || '');
  const [bannerPreview, setBannerPreview] = useState(initialData.banner || '');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => () => { if (logoPreview.startsWith('blob:')) URL.revokeObjectURL(logoPreview); }, [logoPreview]);
  useEffect(() => () => { if (bannerPreview.startsWith('blob:')) URL.revokeObjectURL(bannerPreview); }, [bannerPreview]);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Store logo must be an image.');
      return;
    }

    // 5 MB limit
    if (file.size > 5 * 1024 * 1024) {
      setError('Store logo must be less than 5MB.');
      return;
    }

    setError(null);
    setLogo(file);

    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoPreview(URL.createObjectURL(file));
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Store banner must be an image.');
      return;
    }

    // 10 MB limit
    if (file.size > 10 * 1024 * 1024) {
      setError('Store banner must be less than 10MB.');
      return;
    }

    setError(null);
    setBanner(file);

    if (bannerPreview) {
      URL.revokeObjectURL(bannerPreview);
    }

    setBannerPreview(URL.createObjectURL(file));
  };

  const removeLogo = () => {
    setLogo(null);

    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoPreview('');
  };

  const removeBanner = () => {
    setBanner(null);

    if (bannerPreview) {
      URL.revokeObjectURL(bannerPreview);
    }

    setBannerPreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (documentFiles.some((file, index) => !documentLabels[index]?.trim())) { setError('Label every supporting document.'); return; }
    if (!documentFiles.length && !initialData.supportingDocumentCount) { setError('Upload at least one supporting document photo.'); return; }

    if (!pickupLocation || !address.trim()) { setError('Confirm your store pickup location on the map.'); return; }
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      formData.append('logoRemoved', String(!logoPreview));
      formData.append('bannerRemoved', String(!bannerPreview));
      formData.append('businessHours', JSON.stringify(businessHours));
      formData.append('showRegistrationDetails', String(showRegistrationDetails));
      formData.append('supportingDocumentLabels', JSON.stringify(documentLabels));
      formData.append('businessTinNumber', businessTinNumber.trim());
      formData.append('dtiRegistrationNumber', dtiRegistrationNumber.trim());
      formData.append('permitNumber', permitNumber.trim());
      documentFiles.forEach((file) => formData.append('supportingDocuments', file));
      formData.append('storeName', storeName.trim());
      formData.append('vendorCategory', vendorCategory);
      formData.append('description', description.trim());
      formData.append('completeAddress', address.trim());
      formData.append('pickupLocation', JSON.stringify(pickupLocation));

      if (logo) {
        formData.append('logo', logo);
      }

      if (banner) {
        formData.append('banner', banner);
      }
      const response = await post('/vendors/onboard', formData);

      if (response.success) {
        onComplete(response.vendor);
      } else {
        setError(
          response.message || 'Failed to create vendor profile'
        );
      }
    } catch (err) {
      console.error('Onboarding error:', err);

      setError(
        err.response?.data?.message ||
        'An error occurred during onboarding'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-w-0">
      <header className="mb-5">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-primary">Store application</p>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Setup Your Store</h1>
        <p className="mt-2 text-sm leading-6 text-gray-500">Welcome, {user?.name}! Let's get your store ready.</p>
      </header>
      <form onSubmit={handleSubmit} className={`${styles.form} min-w-0 space-y-5`} aria-busy={loading}>
        {error && <div role="alert" className="rounded-xl bg-red-50 px-3 py-3 text-sm text-red-700">{error}</div>}

        <section className="space-y-3" aria-labelledby="onboarding-basics">
          <h2 id="onboarding-basics" className="text-base font-semibold text-gray-900">Store basics</h2>
          <label className={styles.field}>Store Name
            <input type="text" placeholder="e.g. Maria's Kitchen" value={storeName} onChange={(e) => setStoreName(e.target.value)} required />
          </label>
          <label className={styles.field}>Store product category
            <select required value={vendorCategory} onChange={(e) => setVendorCategory(e.target.value)}>
              <option value="">Select category</option>
              {PRODUCT_CATEGORIES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className={styles.field}>Description
            <textarea rows={3} placeholder="Briefly describe your store..." value={description} onChange={(e) => setDescription(e.target.value)} />
          </label>
        </section>

        <section className="space-y-3 border-t border-gray-100 pt-5" aria-labelledby="onboarding-location">
          <h2 id="onboarding-location" className="text-base font-semibold text-gray-900">Store location</h2>
          <div className={styles.pickupMap}>
            <LocationPicker label="Store pickup location" pointerLabel="Pickup here" value={pickupLocation} resolveAddress addressLookupEndpoint="/platform-map/resolve-address" onChange={(location, result) => { setPickupLocation(location); setAddress(result.completeAddress); }} disabled={loading} />
          </div>
          <p className="text-sm text-gray-600"><strong>Store address:</strong> {address || 'Confirm a map location to generate your store address.'}</p>
        </section>

        <section className="space-y-3 border-t border-gray-100 pt-5" aria-labelledby="onboarding-appearance">
          <h2 id="onboarding-appearance" className="text-base font-semibold text-gray-900">Store appearance</h2>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Store Banner</p>
            <div className="relative">
              {bannerPreview ? (
                <>
                  <img src={bannerPreview} alt="Store banner preview" className="h-36 w-full rounded-xl object-cover sm:h-44" />
                  <button type="button" onClick={removeBanner} className="absolute right-2 top-2 flex min-h-10 items-center gap-1 rounded-full bg-black/70 px-3 text-xs font-medium text-white hover:bg-black"><X size={14} aria-hidden="true" />Remove</button>
                </>
              ) : (
                <label htmlFor="banner" className="flex h-36 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-primary/30 bg-primary/5 text-center transition hover:bg-primary/10 sm:h-44">
                  <ImagePlus size={26} className="text-primary" aria-hidden="true" />
                  <span className="text-sm font-semibold text-gray-700">Upload Store Banner</span>
                  <span className="text-xs text-gray-500">1200 × 400 px · JPG, PNG, WEBP · Max 10MB</span>
                  <input id="banner" type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={handleBannerChange} />
                </label>
              )}
            </div>
          </div>
          <div className="flex min-w-0 items-center gap-3">
            {logoPreview ? (
              <div className="relative shrink-0">
                <img src={logoPreview} alt="Store logo preview" className="h-20 w-20 rounded-2xl border border-gray-100 object-cover" />
                <button type="button" onClick={removeLogo} aria-label="Remove store logo" className="absolute -right-2 -top-2 flex h-9 w-9 items-center justify-center rounded-full bg-gray-900 text-white"><X size={16} aria-hidden="true" /></button>
              </div>
            ) : (
              <label htmlFor="logo" className="flex h-20 w-20 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-primary/30 bg-primary/5 text-primary hover:bg-primary/10">
                <Store size={24} aria-hidden="true" /><span className="text-xs font-medium">Upload</span>
                <input id="logo" type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={handleLogoChange} />
              </label>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-700">Store Logo</p>
              <p className="mt-1 text-xs leading-5 text-gray-500">512 × 512 px recommended<br />JPG, PNG, WEBP · Max 5MB</p>
            </div>
          </div>
        </section>

        <section className="space-y-3 border-t border-gray-100 pt-5" aria-labelledby="onboarding-registration">
          <h2 id="onboarding-registration" className="text-base font-semibold text-gray-900">Business registration</h2>
          {[
            ['Business TIN number', businessTinNumber, setBusinessTinNumber],
            ['DTI registration number', dtiRegistrationNumber, setDtiRegistrationNumber],
            ['Permit number', permitNumber, setPermitNumber],
          ].map(([label, value, setValue]) => <label key={label} className={styles.field}>{label}<input required maxLength={80} value={value} onChange={(e) => setValue(e.target.value)} placeholder={`Enter ${label.toLowerCase()}`} /></label>)}
          <div className="space-y-2">
            <label className={styles.field}>Supporting document photos
              <input type="file" multiple required={!initialData.supportingDocumentCount && !documentFiles.length} accept="image/jpeg,image/png,image/webp" className={styles.fileInput} onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 3 || files.some((file) => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 4 * 1024 * 1024)) {
                  setError('Choose 1 to 3 JPEG, PNG, or WebP photos, up to 4 MB each.'); e.target.value = ''; return;
                }
                setDocumentLabels(files.map(() => '')); setDocumentFiles(files); setError(null);
              }} />
            </label>
            <p className="text-xs leading-5 text-gray-500">Upload clear photos of your business registration and permit. Up to 3 photos, 4 MB each. These documents are visible only to you and administrators. Selecting new photos replaces the previous set.</p>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
            {documentPreviews.length ? documentPreviews.map((src, i) => (
              <div key={src} className="min-w-0 space-y-2">
                <label className={styles.field}>Document {i + 1} label<input required maxLength={100} placeholder="e.g. DTI certificate, Business permit" value={documentLabels[i] || ''} onChange={e => setDocumentLabels(labels => labels.map((label, index) => index === i ? e.target.value : label))} /></label>
                <img src={src} alt={documentLabels[i] || 'Supporting document ' + (i + 1)} className="h-40 w-full rounded-lg bg-gray-50 object-contain" />
              </div>
            )) : Array.from({ length: initialData.supportingDocumentCount || 0 }, (_, i) => <PrivateDocumentPhoto key={i} path={'/vendors/application/documents/' + i} label={initialData.supportingDocumentLabels?.[i] || 'Supporting document ' + (i + 1)} />)}
          </div>
        </section>

        <section className="space-y-3 border-t border-gray-100 pt-5" aria-label="Business hours">
          <BusinessHoursFields value={businessHours} onChange={setBusinessHours} />
        </section>
        <Button type="submit" className="min-h-12 w-full gap-2 rounded-xl px-3 py-3 text-sm" disabled={loading}>
          {loading ? <LoaderCircle size={18} className="animate-spin" aria-hidden="true" /> : <Upload size={18} aria-hidden="true" />}
          {loading ? 'Submitting...' : initialData._id ? 'Resubmit application' : 'Submit application for review'}
        </Button>
      </form>
    </div>
  );
};

export default Onboarding;
